<template>
  <transition name="app-splash-fade" @after-leave="emit('done')">
    <div v-if="visible" class="app-splash">
      <LottiePlayer
        class="app-splash-lottie"
        :src="splashSrc"
        size="min(58vw, 250px)"
        :loop="false"
        :autoplay="true"
        @error="onLottieError"
      />
    </div>
  </transition>
</template>

<script setup lang="ts">
// Animated boot splash (native only). Sequence, all on the SAME #003250 ground so
// there is no flash between stages:
//   native launch splash  →  this Lottie overlay  →  the app
// The native launch screen stays up (SplashScreen launchAutoHide=false in
// capacitor.config.json) until THIS overlay has painted; then we hide the native
// splash, let the Lottie play, and fade out to reveal the app.
import { ref, onMounted } from 'vue'
import LottiePlayer from 'src/components/LottiePlayer.vue'
import { flavorImageDir } from 'src/data'
import usePlatform from 'src/composables/usePlatform'

const emit = defineEmits<{ (e: 'done'): void }>()

const { isCapacitor } = usePlatform()
const flavor = process.env.BUSINESS_TYPE || 'table'
const splashSrc = `/flavor_images/${flavorImageDir(flavor)}/flv_lottie_splash.json`

// How long the animated splash holds before fading to the app, and a hard safety
// ceiling so a stalled Lottie can never trap the user on the splash.
const SHOW_MS = 2500
const SAFETY_MS = 6000

const visible = ref(true)

async function hideNativeSplash() {
  if (!isCapacitor) return
  try {
    const { SplashScreen } = await import('@capacitor/splash-screen')
    await SplashScreen.hide({ fadeOutDuration: 250 })
  } catch { /* web / plugin unavailable — no-op */ }
}

function dismiss() { visible.value = false }

function onLottieError() {
  // Lottie JSON missing/failed → don't sit on a blank splash.
  void hideNativeSplash()
  dismiss()
}

onMounted(() => {
  // Paint our overlay first, THEN drop the native launch splash — same navy
  // ground, so the handoff is invisible.
  requestAnimationFrame(() => { void hideNativeSplash() })
  setTimeout(dismiss, SHOW_MS)
  setTimeout(() => { if (visible.value) dismiss() }, SAFETY_MS)
})
</script>

<style scoped>
.app-splash {
  position: fixed;
  inset: 0;
  z-index: 99999;
  background: #f2f3f5;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Sit above the iOS notch / Android cutouts so the ground is edge-to-edge.
     Left/right have no --dd-safe token (never needed elsewhere); env() alone is fine
     there and 0 on Android <140 is harmless for a centered splash. */
  padding: var(--dd-safe-top) env(safe-area-inset-right) var(--dd-safe-bottom) env(safe-area-inset-left);
}
.app-splash-fade-leave-active {
  transition: opacity 0.45s ease;
}
.app-splash-fade-leave-to {
  opacity: 0;
}
@media (prefers-reduced-motion: reduce) {
  .app-splash-fade-leave-active { transition: none; }
}
</style>
